import React, { useEffect, useState, useRef } from 'react';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, UserPlus, Users, Upload, Download, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

export default function EmployeesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [underQ, setUnderQ] = useState(null);
  const [showUnderQ, setShowUnderQ] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const [emps, uq] = await Promise.all([hrAPI.listEmployees(), hrAPI.underQualified()]);
      setRows(emps.data || []); setUnderQ(uq.data || null);
    }
    catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try { const r = await hrAPI.syncEmployees(); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const save = async () => {
    if (!dlg.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await hrAPI.createEmployee({ name: dlg.name, emp_code: dlg.emp_code, email: dlg.email });
      toast.success('Employee added');
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const downloadSLTemplate = async () => {
    try {
      const res = await hrAPI.skillLevelTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'Employee_Skill_Levels_Template.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const onImportSL = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await hrAPI.importSkillLevels(fd);
      const d = res.data;
      toast.success(`Updated ${d.updated_entries} entrie(s) across ${d.employees_affected} employee(s)`);
      if (d.errors?.length) toast.warning(`${d.errors.length} row(s) skipped`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const filtered = rows.filter(r =>
    !search || r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.emp_code?.toLowerCase().includes(search.toLowerCase()) ||
    r.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="hr-employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2">
            <Users className="w-6 h-6 text-pink-600" /> Employees
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">All login users appear here automatically. Non-user staff can be added manually.</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <input type="file" accept=".xlsx" ref={fileRef} onChange={onImportSL} className="hidden" data-testid="hr-sl-import-input" />
            <Button size="sm" variant="outline" onClick={downloadSLTemplate} className="gap-1" data-testid="hr-sl-template"><Download className="w-4 h-4" /> Skill Levels Template</Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="gap-1" data-testid="hr-sl-import"><Upload className="w-4 h-4" /> {importing ? 'Importing...' : 'Import Skill Levels'}</Button>
            <Button size="sm" variant="outline" onClick={sync} disabled={syncing} className="gap-1" data-testid="hr-employees-sync">
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> Sync from Users
            </Button>
            <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={() => setDlg({ name: '', emp_code: '', email: '' })} data-testid="hr-employees-add">
              <UserPlus className="w-4 h-4" /> Add Employee
            </Button>
          </div>
        )}
      </div>

      {underQ && underQ.count > 0 && (
        <Card className="border border-amber-200 bg-amber-50/60 shadow-none" data-testid="hr-under-qualified-tile">
          <CardContent className="p-4">
            <button className="flex items-center justify-between w-full text-left" onClick={() => setShowUnderQ(v => !v)} data-testid="hr-under-qualified-toggle">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">{underQ.count} employee{underQ.count > 1 ? 's' : ''} assigned to position{underQ.count > 1 ? 's' : ''} they're not eligible for</span>
              </div>
              {showUnderQ ? <ChevronDown className="w-4 h-4 text-amber-700" /> : <ChevronRight className="w-4 h-4 text-amber-700" />}
            </button>
            {showUnderQ && (
              <div className="mt-3 space-y-2">
                {underQ.employees.map(e => (
                  <div key={e.employee_id} className="flex flex-wrap items-center gap-2 text-xs border-t border-amber-200 pt-2" data-testid={`hr-under-qualified-row-${e.employee_id}`}>
                    <button className="font-medium text-pink-700 hover:underline" onClick={() => navigate(`/hr/employees/${e.employee_id}`)}>{e.name}</button>
                    <span className="text-slate-500">({e.qualification}, {e.experience_years}yr)</span>
                    <div className="flex flex-wrap gap-1">
                      {e.positions.map(p => (
                        <Badge key={p.fp_id} variant="outline" className="text-[10px] bg-white text-amber-700 border-amber-300">{p.fp_name} — needs {p.needs}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search by name, code or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="hr-employees-search" />
      </div>

      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold text-slate-500">Name</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Emp Code</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Email</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Type</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8 text-sm">No employees</TableCell></TableRow>
              ) : filtered.map(r => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/hr/employees/${r.id}`)} data-testid={`hr-employee-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm font-mono text-slate-500">{r.emp_code || '—'}</TableCell>
                  <TableCell className="text-sm text-slate-500">{r.email || '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${r.is_user ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{r.is_user ? 'Login User' : 'Non-user'}</Badge>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{r.status || 'active'}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="hr-employee-dialog">
          <DialogHeader>
            <DialogTitle>Add Employee (non-user)</DialogTitle>
            <DialogDescription className="text-xs">For shop-floor staff without a login account</DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Name *</Label><Input value={dlg.name} onChange={e => setDlg({ ...dlg, name: e.target.value })} data-testid="hr-employee-name-input" autoFocus /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Emp Code</Label><Input value={dlg.emp_code} onChange={e => setDlg({ ...dlg, emp_code: e.target.value })} data-testid="hr-employee-code-input" /></div>
                <div><Label className="text-xs">Email</Label><Input value={dlg.email} onChange={e => setDlg({ ...dlg, email: e.target.value })} data-testid="hr-employee-email-input" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-employee-save">{saving ? 'Saving...' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
